import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { X, ChevronLeft, ChevronRight, ZoomIn, ZoomOut } from "lucide-react";

/**
 * Full-screen image lightbox — click to open, escape/click-outside to close,
 * arrow keys to navigate, tap/click on the image to toggle 2x zoom.
 *
 * Props:
 *   images · array of image URLs (strings)
 *   startIndex · initial index (default 0)
 *   onClose · () => void  (required)
 *   alt · optional alt-text base
 */
export default function Lightbox({ images = [], startIndex = 0, onClose, alt = "" }) {
    const [idx, setIdx] = useState(startIndex);
    const [zoomed, setZoomed] = useState(false);
    const [pan, setPan] = useState({ x: 0, y: 0 });
    const dragRef = useRef(null);

    const total = images.length;

    const next = useCallback(() => {
        setZoomed(false); setPan({ x: 0, y: 0 });
        setIdx((i) => (i + 1) % total);
    }, [total]);
    const prev = useCallback(() => {
        setZoomed(false); setPan({ x: 0, y: 0 });
        setIdx((i) => (i - 1 + total) % total);
    }, [total]);

    // Keyboard
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === "Escape") onClose?.();
            else if (e.key === "ArrowRight") next();
            else if (e.key === "ArrowLeft") prev();
            else if (e.key === " " || e.key === "Enter") { e.preventDefault(); setZoomed((z) => !z); }
        };
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [next, prev, onClose]);

    // Lock body scroll
    useEffect(() => {
        const prevOverflow = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prevOverflow; };
    }, []);

    // Drag-pan when zoomed
    const onMouseDown = (e) => {
        if (!zoomed) return;
        dragRef.current = { startX: e.clientX - pan.x, startY: e.clientY - pan.y };
    };
    const onMouseMove = (e) => {
        if (!dragRef.current || !zoomed) return;
        setPan({ x: e.clientX - dragRef.current.startX, y: e.clientY - dragRef.current.startY });
    };
    const stopDrag = () => { dragRef.current = null; };

    if (!total) return null;
    const src = images[Math.max(0, Math.min(idx, total - 1))];

    return createPortal(
        <div
            className="fixed inset-0 z-[2000] bg-black/95 flex flex-col select-none"
            onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
            onMouseMove={onMouseMove}
            onMouseUp={stopDrag}
            onMouseLeave={stopDrag}
            data-testid="lightbox"
        >
            {/* Top bar */}
            <div className="flex items-center justify-between px-4 py-3 text-white/80 text-xs">
                <div className="font-mono">
                    {total > 1 ? `${idx + 1} / ${total}` : ""}
                </div>
                <div className="flex items-center gap-3">
                    <button
                        onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); setPan({ x: 0, y: 0 }); }}
                        className="hover:text-[var(--tb-gold)] p-1.5 transition"
                        title={zoomed ? "Reducir (Enter)" : "Ampliar (Enter)"}
                        data-testid="lightbox-zoom">
                        {zoomed ? <ZoomOut className="w-5 h-5" /> : <ZoomIn className="w-5 h-5" />}
                    </button>
                    <button
                        onClick={(e) => { e.stopPropagation(); onClose?.(); }}
                        className="hover:text-[var(--tb-gold)] p-1.5 transition"
                        title="Cerrar (Esc)"
                        data-testid="lightbox-close">
                        <X className="w-6 h-6" />
                    </button>
                </div>
            </div>

            {/* Main image */}
            <div className="flex-1 relative flex items-center justify-center overflow-hidden">
                {total > 1 && (
                    <button
                        onClick={(e) => { e.stopPropagation(); prev(); }}
                        className="absolute left-3 md:left-6 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/80 text-white rounded-full w-11 h-11 md:w-14 md:h-14 flex items-center justify-center transition"
                        aria-label="Anterior"
                        data-testid="lightbox-prev">
                        <ChevronLeft className="w-6 h-6 md:w-8 md:h-8" />
                    </button>
                )}

                <img
                    src={src}
                    alt={alt}
                    onClick={(e) => { e.stopPropagation(); setZoomed((z) => !z); setPan({ x: 0, y: 0 }); }}
                    onMouseDown={onMouseDown}
                    onDragStart={(e) => e.preventDefault()}
                    style={{
                        transform: `translate(${pan.x}px, ${pan.y}px) scale(${zoomed ? 2.4 : 1})`,
                        transition: dragRef.current ? "none" : "transform 220ms ease",
                        cursor: zoomed ? (dragRef.current ? "grabbing" : "grab") : "zoom-in",
                    }}
                    className="max-h-[85vh] max-w-[92vw] object-contain"
                    data-testid="lightbox-image"
                />

                {total > 1 && (
                    <button
                        onClick={(e) => { e.stopPropagation(); next(); }}
                        className="absolute right-3 md:right-6 top-1/2 -translate-y-1/2 z-10 bg-black/50 hover:bg-black/80 text-white rounded-full w-11 h-11 md:w-14 md:h-14 flex items-center justify-center transition"
                        aria-label="Siguiente"
                        data-testid="lightbox-next">
                        <ChevronRight className="w-6 h-6 md:w-8 md:h-8" />
                    </button>
                )}
            </div>

            {/* Thumb strip */}
            {total > 1 && (
                <div className="flex gap-2 overflow-x-auto px-4 py-3 border-t border-white/10" data-testid="lightbox-thumbs">
                    {images.map((s, i) => (
                        <button
                            key={i}
                            onClick={(e) => { e.stopPropagation(); setZoomed(false); setPan({ x: 0, y: 0 }); setIdx(i); }}
                            className={`flex-shrink-0 w-14 h-14 md:w-16 md:h-16 rounded overflow-hidden border-2 transition ${
                                i === idx ? "border-[var(--tb-gold)]" : "border-white/20 opacity-60 hover:opacity-100"
                            }`}
                            data-testid={`lightbox-thumb-${i}`}>
                            <img src={s} alt="" className="w-full h-full object-cover" />
                        </button>
                    ))}
                </div>
            )}

            {/* Bottom hint */}
            <div className="text-center text-white/40 text-[10px] pb-2">
                Esc para cerrar · ← → para navegar · Enter/clic para zoom
            </div>
        </div>,
        document.body
    );
}
